import React, { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, FilePlus2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MobilePage, GlassCard } from "@/components/mobile/MobilePrimitives";

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
};

function StatusPill({ status }) {
  const s = String(status || "pending").toLowerCase();
  const cls =
    s === "approved"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : s === "rejected"
      ? "bg-red-50 text-red-700 border-red-100"
      : "bg-amber-50 text-amber-700 border-amber-100";

  return <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black uppercase ${cls}`}>{s}</span>;
}

function RequestModal({ type, onClose, onSaved }) {
  const [form, setForm] = useState({ type: "vacation", from_date: "", to_date: "", days: 1, reason: "" });
  const [busy, setBusy] = useState(false);
  // Only offer leave the employee can actually file. A hardcoded list offered
  // "Emergency Leave" (no such policy) and paternity to everyone, and the
  // server refused both.
  const [types, setTypes] = useState([]);
  useEffect(() => {
    if (type === "ot") return;
    api.get("/me/leave-balances")
      .then((r) => setTypes((r.data?.balances || []).filter((b) => b.applicable)))
      .catch(() => setTypes([]));
  }, [type]);

  const endpoint = type === "ot" ? "/me/overtime" : "/me/leaves";

  const submit = async () => {
    setBusy(true);
    try {
      // The API's contract is leave_type / start_date / end_date. This form had
      // always posted type / from_date / to_date, so mobile leave filing never
      // once succeeded.
      const body = type === "ot" ? form : {
        leave_type: form.type,
        start_date: form.from_date,
        end_date: form.to_date,
        reason: form.reason,
      };
      await api.post(endpoint, body);
      toast.success("Request submitted");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="mx-auto max-w-md rounded-[30px] bg-white p-5 shadow-2xl">
        <div className="flex justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-slate-500">New Request</div>
            <div className="text-2xl font-black">{type === "ot" ? "Overtime" : "Leave"}</div>
          </div>
          <button onClick={onClose} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-bold">Close</button>
        </div>

        <div className="mt-5 space-y-3">
          {type !== "ot" && (
            <select className="w-full rounded-2xl border p-3" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {[...(types.length ? types : [{ leave_type: "vacation", name: "Vacation Leave" }]), { leave_type: "leave_without_pay", name: "Leave without pay (deducted from pay)" }].map((b) => (
                <option key={b.leave_type} value={b.leave_type}>
                  {b.name || b.leave_type}
                  {b.remaining_days != null ? ` (${b.remaining_days} left)` : ""}
                </option>
              ))}
            </select>
          )}
          <input className="w-full rounded-2xl border p-3" type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} />
          <input className="w-full rounded-2xl border p-3" type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
          {type === "ot" && (
            <input className="w-full rounded-2xl border p-3" type="number" placeholder="Hours" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} />
          )}
          <textarea className="w-full rounded-2xl border p-3" placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>

        <button onClick={submit} disabled={busy} className="mt-5 w-full rounded-2xl bg-blue-700 px-4 py-4 font-black text-white">
          {busy ? "Submitting..." : "Submit Request"}
        </button>
      </div>
    </div>
  );
}

export default function MobileMyTime() {
  const [tab, setTab] = useState("leaves");
  const [leaves, setLeaves] = useState([]);
  const [ot, setOt] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [modal, setModal] = useState(null);

  const load = async () => {
    const [l, o, a] = await Promise.allSettled([
      api.get("/me/leaves"),
      api.get("/me/overtime"),
      api.get("/me/attendance"),
    ]);

    if (l.status === "fulfilled") setLeaves(l.value.data.items || []);
    if (o.status === "fulfilled") setOt(o.value.data.items || []);
    if (a.status === "fulfilled") setAttendance(a.value.data.items || []);
  };

  useEffect(() => {
    load();
  }, []);

  const rows = tab === "leaves" ? leaves : tab === "ot" ? ot : attendance;

  return (
    <MobilePage>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-500">Employee Self Service</div>
          <div className="text-3xl font-black">My Time</div>
        </div>
        <button onClick={() => setModal(tab === "ot" ? "ot" : "leave")} className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700 text-white shadow-lg">
          <Plus className="h-6 w-6" />
        </button>
      </div>

      <GlassCard className="grid grid-cols-3 gap-2 p-2">
        {[
          ["leaves", "Leaves"],
          ["ot", "Overtime"],
          ["attendance", "Attendance"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-2xl px-3 py-3 text-sm font-black ${tab === k ? "bg-blue-700 text-white" : "text-slate-500"}`}
          >
            {label}
          </button>
        ))}
      </GlassCard>

      <div className="space-y-3">
        {rows.length === 0 && (
          <GlassCard className="p-6 text-center">
            <CalendarDays className="mx-auto h-10 w-10 text-slate-400" />
            <div className="mt-3 font-black">No records yet</div>
            <div className="text-sm text-slate-500">Your records will appear here.</div>
          </GlassCard>
        )}

        {tab !== "attendance" &&
          rows.map((x) => (
            <GlassCard key={x.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {tab === "ot" ? <Clock className="h-5 w-5 text-violet-600" /> : <FilePlus2 className="h-5 w-5 text-blue-600" />}
                    <div className="text-lg font-black capitalize">{x.type || (tab === "ot" ? "Overtime" : "Leave")}</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-500">{fmtDate(x.from_date || x.date)} - {fmtDate(x.to_date || x.date)}</div>
                  <div className="mt-1 text-sm">{x.reason || "No reason provided"}</div>
                </div>
                <StatusPill status={x.status} />
              </div>
            </GlassCard>
          ))}

        {tab === "attendance" &&
          rows.map((x) => (
            <GlassCard key={x.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-black">{fmtDate(x.date || x.created_at)}</div>
                  <div className="mt-1 text-sm text-slate-500">In: {x.time_in || "-"} · Out: {x.time_out || "-"}{x.work_from_home ? " · Home" : ""}{x.break_source === "recorded" ? ` · Break ${x.break_minutes}m` : ""}</div>
                </div>
                <div className="text-right">
                  <CheckCircle2 className="ml-auto h-6 w-6 text-emerald-600" />
                  <div className="mt-1 text-sm font-black">{x.hours_worked || x.total_hours || 0}h</div>
                </div>
              </div>
            </GlassCard>
          ))}
      </div>

      {modal && <RequestModal type={modal} onClose={() => setModal(null)} onSaved={load} />}
    </MobilePage>
  );
}
